import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CreditCardOutlined,
  DollarOutlined,
  LeftOutlined,
  ShopOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { timestampToLocalTime } from "anka-endeavour";
import { Card, Col, Descriptions, Row, Spin, Statistic, Tag, Typography, message } from "antd";
import axios from "axios";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import InlineCopyable from "../components/InlineCopyable";
import { hostname } from "../hooks/useEndeavour";
import Status from "../components/Status";

const { Title, Text } = Typography;

function TransactionPage() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [transaction, setTransaction] = useState(null);

  const query = useQuery({
    queryKey: ["transaction"],
    queryFn: () => {
      return axios
        .get(`${hostname}/pa-alliance/transactions/${uuid}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        })
        .then((res) => {
          setLoading(false);
          setTransaction(res.data.payload.data[0]);
          return res.data.payload.data[0];
        })
        .catch((err) => {
          setLoading(false);
          message.error("Failed to fetch transaction data");
          console.error(err);
        });
    },
  });

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "100px 0" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!transaction) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Text type="secondary">Transaction not found</Text>
        </div>
      </Card>
    );
  }

  return (
    <Row>
      <Col span={24}>
        <Title level={2} onClick={() => navigate("/transactions")} style={{ cursor: "pointer" }}>
          <LeftOutlined />
          Transaction Details
        </Title>

        {/* Summary Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="Amount"
                value={transaction.amount}
                precision={2}
                suffix={transaction.currency}
                valueStyle={{ color: transaction.amount >= 0 ? "#3f8600" : "#cf1322" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card>
              {transaction.gross_amount !== null ? (
                <Statistic title="Gross Amount" value={transaction.gross_amount} precision={2} suffix={transaction.currency} />
              ) : (
                <Statistic title="Gross Amount" value={"-"} />
              )}
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="Authorization Amount"
                value={transaction.authorization_amount}
                precision={2}
                suffix={transaction.authorization_currency}
              />
            </Card>
          </Col>
        </Row>
        {/* Account & Card Information */}
        <Card
          title={
            <>
              <CreditCardOutlined /> Account & Card Information
            </>
          }
          style={{ marginBottom: 16 }}
        >
          <Descriptions bordered column={{ xs: 1, sm: 1, md: 1 }}>
            <Descriptions.Item label="User Email">
              <InlineCopyable>{transaction.user_email}</InlineCopyable>
            </Descriptions.Item>
            <Descriptions.Item label="Card Last 4">{transaction.card_info.last4}</Descriptions.Item>

            <Descriptions.Item label="Card Status">
              <Status value={transaction.card_info.card_status} />
            </Descriptions.Item>
          </Descriptions>
        </Card>
        {/* Basic Information */}
        <Card
          title={
            <>
              <CreditCardOutlined /> Basic Information
            </>
          }
          style={{ marginBottom: 16 }}
        >
          <Descriptions bordered column={{ xs: 1, sm: 2, md: 2 }}>
            <Descriptions.Item label="Transaction UUID">
              <InlineCopyable>{transaction.card_transaction_uuid || transaction.uuid}</InlineCopyable>
            </Descriptions.Item>
            <Descriptions.Item label="Reference ID">
              <InlineCopyable>{transaction.ref_id}</InlineCopyable>
            </Descriptions.Item>
            <Descriptions.Item label="ID">{transaction.id}</Descriptions.Item>
            <Descriptions.Item label="Type">{transaction.type}</Descriptions.Item>
            <Descriptions.Item label="Intent">{transaction.intent}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Status value={transaction.status} />
            </Descriptions.Item>
            <Descriptions.Item label="Description" span={2}>
              {transaction.description || "-"}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Merchant Information */}
        <Card
          title={
            <>
              <ShopOutlined /> Merchant Information
            </>
          }
          style={{ marginBottom: 16 }}
        >
          <Descriptions bordered column={{ xs: 1, sm: 2, md: 2 }}>
            <Descriptions.Item label="Merchant Name">
              <Text strong>{transaction.merchant_name || "-"}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="MCC Code">
              <Tag color="blue">{transaction.merchant_mcc || "-"}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Country">{transaction.merchant_country || "-"}</Descriptions.Item>
            <Descriptions.Item label="Category">{transaction.merchant_category || "-"}</Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Amount Details */}
        <Card
          title={
            <>
              <DollarOutlined /> Amount Details
            </>
          }
          style={{ marginBottom: 16 }}
        >
          <Descriptions bordered column={{ xs: 1, sm: 2, md: 2 }}>
            <Descriptions.Item label="Amount">
              <Statistic
                value={transaction.amount}
                precision={2}
                suffix={transaction.currency}
                valueStyle={{ color: transaction.amount >= 0 ? "#3f8600" : "#cf1322" }}
              />
            </Descriptions.Item>

            <Descriptions.Item label="Gross Amount">
              {transaction.gross_amount !== null ? (
                <Statistic value={transaction.gross_amount} precision={2} suffix={transaction.currency} />
              ) : (
                <Statistic value={"-"} />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Authorization Amount">
              <Statistic
                value={transaction.authorization_amount}
                precision={2}
                suffix={transaction.authorization_currency}
                valueStyle={{ color: transaction.authorization_amount >= 0 ? "#3f8600" : "#cf1322" }}
              />
            </Descriptions.Item>

            <Descriptions.Item label="Authorization Response">{transaction.authorization_response_message || "-"}</Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Timestamps */}
        <Card
          title={
            <>
              <ClockCircleOutlined /> Timestamps
            </>
          }
        >
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Transaction DateTime">{transaction.datetime}</Descriptions.Item>
            <Descriptions.Item label="Posted Time">{transaction.posted_at ? timestampToLocalTime(transaction.posted_at) : "-"}</Descriptions.Item>
            <Descriptions.Item label="Created Time">{timestampToLocalTime(transaction.created_at)}</Descriptions.Item>
            <Descriptions.Item label="Updated Time">{timestampToLocalTime(transaction.updated_at)}</Descriptions.Item>
          </Descriptions>
        </Card>
      </Col>
    </Row>
  );
}

export default TransactionPage;
